import { Accordion, AccordionItem } from '@design-system';
import { formatDate } from '@utils/datetime';
import { isReleaseTypeBlockchainAuction } from '@utils/product';
import {
  isStoryblokRichTextPresent,
  renderStoryblokRichText,
  stringToStoryblokRichText,
} from '@utils/renderStoryblokRichText';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { pathFor } from '../../../helpers/links';
import { useCertificateOfAuthenticity } from '../../../hooks/useCertificateOfAuthenticity';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useTaxTypeTransactionDetails } from '../../../hooks/useTaxTypeTransactionDetails';
import { DrawStage } from '../../../types/Draw';
import { GeneratedTaxType } from '../../../types/generated';
import {
  AssetStoryblok,
  FramingDetailsStoryblok,
  NftAuctionStoryblok,
  PackagingDetailsStoryblok,
  RichtextStoryblok,
} from '../../../types/generated-storyblok';
import {
  FrameOptionConsolidated,
  ProductBenefits,
} from '../../../types/Products';
import { Image } from '../../Image';
import { RichTextLink } from '../../RichTextLink';

type ProductAccordionProps = {
  auction?: NftAuctionStoryblok;
  open?: boolean;
  productId: string;
  editionSize?: number;
  releaseType?: StoryblokReleaseType;
  description?: RichtextStoryblok;
  customDimensions?: RichtextStoryblok;
  framingDetails?: string | FramingDetailsStoryblok;
  framingOptions?: FrameOptionConsolidated[] | null;
  isFrameIncluded?: boolean;
  isPreOrder?: boolean;
  packagingDetails?: PackagingDetailsStoryblok;
  medium?: RichtextStoryblok;
  authentication?: RichtextStoryblok;
  tags?: string[];
  finishing?: string;
  eligibility?: RichtextStoryblok;
  launchDate?: string;
  isShippingRequired?: boolean;
  noTaxCharged?: boolean;
  benefits?: ProductBenefits | null;
  drawStage?: DrawStage;
  onFramingDetailsClick?: () => void;
  onTaxDetailsClick?: () => void;
  noCoa: boolean;
};

export const ProductAccordion = ({
  auction,
  authentication,
  benefits,
  customDimensions,
  description,
  editionSize,
  eligibility,
  finishing,
  framingDetails,
  framingOptions,
  isFrameIncluded,
  isPreOrder,
  isShippingRequired,
  launchDate,
  medium,
  noCoa,
  noTaxCharged,
  onFramingDetailsClick,
  onTaxDetailsClick,
  open = true,
  packagingDetails,
  productId,
  releaseType,
  tags,
}: ProductAccordionProps) => {
  const { t } = useTranslation('product');
  const [countryCode] = useCountryCode();
  const taxes = useTaxTypeTransactionDetails(countryCode);

  const isDrawerNotNeeded =
    (taxes?.taxType === GeneratedTaxType.Vat ||
      taxes?.taxType === GeneratedTaxType.NoTax) &&
    benefits?.hasFreeShipping;

  const isNftAuction =
    releaseType && isReleaseTypeBlockchainAuction(releaseType);
  const certificate = useCertificateOfAuthenticity(noCoa, tags || []);

  const isDraw = releaseType === 'draw-release';
  const isAuction = releaseType === 'auction';

  const hasPackagingDetails = packagingDetails !== undefined;
  const showShippingTaxes =
    isShippingRequired && !noTaxCharged && taxes && !isNftAuction;

  const hasCryptoEnabled = benefits?.hasCryptoEnabled ?? false;
  const hasCryptoDrawEnabled = benefits?.hasCryptoDrawEnabled ?? false;
  const hasResaleAgreement = benefits?.hasResaleAgreement ?? false;

  // framingDetails as a string is now outdated
  // new framingDetails should be typeof FramingDetailsStoryblok
  const isFramingDetailsV1 = typeof framingDetails === 'string';

  const framingIndex = 3;
  const packagingIndex = !isFramingDetailsV1 && framingDetails ? 4 : 3;
  const paymentIndex = hasPackagingDetails
    ? packagingIndex + 1
    : packagingIndex;

  const getPackagingDescription = () => {
    if (
      packagingDetails?.description &&
      isStoryblokRichTextPresent(packagingDetails.description)
    ) {
      return renderStoryblokRichText(packagingDetails.description, {
        paragraphClassName: 'pb-0',
      });
    }

    return renderStoryblokRichText(
      stringToStoryblokRichText(getPackagingPreset()?.description ?? ''),
      {
        paragraphClassName: 'pb-0',
      },
    );
  };

  const getPackagingPreset = () => {
    switch (packagingDetails?.preset) {
      case 'print-wooden':
        return {
          description: t('packaging.presets.print-wooden.description'),
          image: t('packaging.presets.print-wooden.image'),
        } as const;
      case 'print-non-wooden':
        return {
          description: t('packaging.presets.print-non-wooden.description'),
          image: t('packaging.presets.print-non-wooden.image'),
        } as const;
      case 'sculpture':
        return {
          description: t('packaging.presets.sculpture.description'),
          image: t('packaging.presets.sculpture.image'),
        } as const;
      default:
        return {} as const;
    }
  };

  const getPackagingImage = (): AssetStoryblok => {
    if (packagingDetails?.image?.filename) {
      return packagingDetails.image;
    }

    return {
      id: 1,
      name: 'Avant Arte Packaging',
      filename: getPackagingPreset()?.image ?? '',
    };
  };

  const getPaymentLabel = () => {
    if (hasCryptoDrawEnabled) {
      return t('benefits.payment.messageDrawCrypto');
    }

    if (isDraw || isPreOrder) {
      return t('benefits.payment.messageDraw');
    }

    if (isNftAuction) {
      return t('benefits.nftAuction');
    }

    return t('benefits.payment.message', {
      crypto: hasCryptoEnabled ? t('benefits.payment.messageCrypto') : '',
    });
  };

  return (
    <Accordion>
      <AccordionItem
        key={`${productId}-aboutThisWork`}
        index={1}
        isOpen={open}
        subject={t('accordion.aboutThisWork')}
      >
        <div className="aa-prose">
          {description && isStoryblokRichTextPresent(description)
            ? render(description, {
                markResolvers: { link: RichTextLink },
              })
            : '–'}
        </div>
      </AccordionItem>
      <AccordionItem
        key={`${productId}-editionDetails`}
        index={2}
        subject={t('accordion.editionDetails')}
      >
        {customDimensions && isStoryblokRichTextPresent(customDimensions) ? (
          <div>
            <p className="font-bold">{t('customDimensionsTitle')}</p>
            {renderStoryblokRichText(customDimensions, {
              paragraphClassName: 'pb-0',
            })}
          </div>
        ) : null}
        {isFramingDetailsV1 && framingDetails ? (
          <div>
            <p className="font-bold">{t('framingOptionsTitle')}</p>
            {framingDetails}
            {!isFrameIncluded && onFramingDetailsClick && (
              <>
                &nbsp;
                <button
                  className="hyperlink"
                  onClick={onFramingDetailsClick}
                  type="button"
                >
                  {t('product:seeDetails')}
                </button>
              </>
            )}
          </div>
        ) : null}
        {medium ? (
          <div>
            <p className="font-bold">{t('mediumTitle')}</p>
            {renderStoryblokRichText(medium, {
              paragraphClassName: 'pb-0',
            })}
          </div>
        ) : null}
        {editionSize ? (
          <div>
            <p className="font-bold">{t('editionSizeLabel')}</p>
            {editionSize}
          </div>
        ) : null}
        {auction?.blockchain ? (
          <div>
            <p className="font-bold">{t('blockchain')}</p>
            {auction.blockchain}
          </div>
        ) : null}
        {auction?.tokenStandard ? (
          <div>
            <p className="font-bold">{t('tokenStandard')}</p>
            {auction.tokenStandard}
          </div>
        ) : null}
        {authentication ? (
          <div>
            <p className="font-bold">{t('authenticationTitle')}</p>
            {renderStoryblokRichText(authentication, {
              paragraphClassName: 'pb-0',
            })}
          </div>
        ) : null}
        {certificate ? (
          <div>
            <p className="font-bold">{t('coaTitle')}</p>
            {certificate}
          </div>
        ) : null}
        {finishing ? (
          <div>
            <p className="font-bold">{t('finishingTitle')}</p>
            {finishing}
          </div>
        ) : null}
        {eligibility && isStoryblokRichTextPresent(eligibility) ? (
          <div>
            <p className="font-bold">{t('eligibilityTitle')}</p>
            {renderStoryblokRichText(eligibility, {
              paragraphClassName: 'pb-0',
            })}
          </div>
        ) : null}
        {launchDate ? (
          <div>
            <p className="font-bold">{t('launchDateTitle')}</p>
            {formatDate(launchDate)}
          </div>
        ) : null}
      </AccordionItem>
      {!isFramingDetailsV1 && framingDetails ? (
        <AccordionItem
          key={`${productId}-framing`}
          index={framingIndex}
          subject={t('accordion.framing.label')}
        >
          <div className="flex w-full flex-col gap-6">
            <div className="aa-prose">
              {framingDetails.description}
              {!isFrameIncluded && onFramingDetailsClick && (
                <>
                  {' '}
                  <button
                    className="hyperlink self-start"
                    onClick={onFramingDetailsClick}
                    type="button"
                  >
                    {t('accordion.framing.moreOptions')}
                  </button>
                </>
              )}
            </div>

            {framingOptions && framingOptions?.length > 0 ? (
              <ul className="flex flex-col gap-3">
                {framingOptions.map((item) => {
                  const image = item.images[0];

                  return (
                    <li key={item.title} className="flex gap-3">
                      <span className="flex h-10 w-10 shrink-0 overflow-hidden rounded-sm bg-red-500">
                        <Image
                          alt={image.alt}
                          className="h-full w-full object-cover"
                          src={image.src}
                          width={48}
                        />
                      </span>
                      <span className="flex flex-col">
                        <span>
                          <strong>{t(`${item.title}`)}</strong>
                        </span>
                        <span>{t(`${item.description}`)}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {framingDetails.showFramingStudio ? (
              <Link
                className="group/card relative flex aspect-[2/1] w-full overflow-hidden text-balance rounded-sm text-md font-bold text-white"
                href="https://avantarte.com/insights/guides/our-framing-studio"
                target="_blank"
              >
                <Image
                  alt={t('accordion.framing.framingStudio')}
                  className="h-full w-full object-cover"
                  size={{
                    sm: { ratio: '4/3', span: 12 },
                    md: { ratio: '4/3', span: 6 },
                    lg: { ratio: '4/3', span: 4 },
                  }}
                  src="https://images.avantarte.org/media/35bA680U3oLQAOJX1HbyXZSil9G"
                />
                <span className="absolute inset-0 flex items-end bg-neutral-6/20 p-4 transition duration-300 ease-out group-hover/card:bg-neutral-6/0">
                  {t('accordion.framing.framingStudio')}
                </span>
              </Link>
            ) : null}
          </div>
        </AccordionItem>
      ) : null}
      {hasPackagingDetails ? (
        <AccordionItem
          key={`${productId}-packaging`}
          index={packagingIndex}
          subject={t('accordion.packaging')}
        >
          {getPackagingDescription()}

          {getPackagingImage().filename !== '' ? (
            <Image
              alt={getPackagingImage().alt || getPackagingImage().name}
              hoverEffect
              shape="rounded"
              size={{
                sm: { ratio: '4/3', span: 12 },
                md: { ratio: '4/3', span: 6 },
                lg: { ratio: '4/3', span: 4 },
              }}
              src={getPackagingImage().filename}
            />
          ) : null}
        </AccordionItem>
      ) : null}
      <AccordionItem
        key={`${productId}-payment`}
        index={paymentIndex}
        subject={
          isNftAuction ? t('accordion.payment') : t('accordion.paymentShipping')
        }
      >
        <div>
          <p className="font-bold">{t('benefits.payment.title')}</p>
          <p>{getPaymentLabel()}</p>
        </div>
        {!isNftAuction ? (
          <div>
            <p className="font-bold">{t('benefits.policies.title')}</p>
            <p>
              {isDraw && (
                <>
                  <Link
                    className="hyperlink"
                    href={pathFor({
                      page: 'SUPPORT',
                      slug: 'terms-and-conditions',
                      anchor: 'draws',
                    })}
                    prefetch={false}
                    target="_blank"
                  >
                    {t('drawPolicy')}
                  </Link>
                  <br />
                </>
              )}
              {isAuction && (
                <>
                  <Link
                    className="hyperlink"
                    href={pathFor({
                      page: 'SUPPORT',
                      slug: 'terms-and-conditions',
                      anchor: 'auctions',
                    })}
                    prefetch={false}
                    target="_blank"
                  >
                    {t('auctionTerms')}
                  </Link>
                  <br />
                </>
              )}
              <Link
                className="hyperlink"
                href={pathFor({ page: 'SUPPORT', slug: 'returns' })}
                prefetch={false}
                target="_blank"
              >
                {t('product:returnsRefunds')}
              </Link>
            </p>
            {hasResaleAgreement && (
              <p className="pt-3">
                {t('benefits.resaleAgreement.required')}
                &nbsp;
                <Link
                  className="hyperlink"
                  href={pathFor({
                    page: 'SUPPORT',
                    slug: 'shipping',
                    anchor: 'what-is-a-resale-agreement',
                  })}
                  prefetch={false}
                  target="_blank"
                >
                  {t('benefits.resaleAgreement.seeDetails')}
                </Link>
              </p>
            )}
          </div>
        ) : null}
        {showShippingTaxes && (
          <div>
            <p className="font-bold">{t('benefits.shipping.title')}</p>
            <p>
              {t(
                benefits?.hasFreeShipping
                  ? taxes.freeDeliveryTranslationCode
                  : taxes.generalTranslationCode,
              )}{' '}
              {!isDrawerNotNeeded && onTaxDetailsClick && (
                <button
                  className="hyperlink"
                  onClick={onTaxDetailsClick}
                  type="button"
                >
                  {t(
                    benefits?.hasFreeShipping
                      ? t('benefits.fees.freeDeliveryDrawerButton')
                      : t('benefits.fees.drawerButton'),
                  )}
                </button>
              )}
            </p>
          </div>
        )}
      </AccordionItem>
    </Accordion>
  );
};
